// src/App.js — bản chuẩn, an toàn cho web1122
import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import ScrollToTop from "./components/ScrollToTop";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { Toaster } from "react-hot-toast";

// Các trang hiện có của site
import Home from "./pages/Home";
import LoginPage from "./pages/LoginPage";
import ServiceDetail from "./pages/ServiceDetail";
import PostForm from "./pages/PostForm";
import GioiThieu from "./pages/GioiThieu";

// Admin
import AdminLayout from "./pages/AdminLayout";
import AdminHome from "./pages/AdminHome";
import AdminUsers from "./pages/AdminUsers";
import AdminServices from "./pages/AdminServices";
import AdminMaintenance from "./pages/AdminMaintenance";
import AdminMaintenanceTools from "./pages/AdminMaintenanceTools";
import AdminPosts from "./pages/AdminPosts";

// Auth & quyền (giữ nguyên logic bạn đang dùng)
import { AuthProvider } from "./auth/useAuth";
import ProtectedRoute from "./auth/ProtectedRoute";
import { ROUTES } from "./auth/acl";

// Trang Quiz (lazy-load)
const Quiz = lazy(() => import("./pages/quiz/Quiz"));

function useRightsMap() {
  return React.useMemo(() => {
    const map = {};
    for (const r of ROUTES) map[r.path] = r.rights;
    return map;
  }, []);
}

function AppShell() {
  const rightsMap = useRightsMap();
  const location = useLocation();
  const isQuizRoute = location.pathname.startsWith("/quiz");

  return (
    <div className="min-h-[100svh] flex flex-col">
      <ScrollToTop />

      {/* Nhánh /quiz chạy độc lập, không bọc Auth, không Header/Footer */}
      {isQuizRoute ? (
        <main className="flex-1 overflow-auto">
          <Routes>
            <Route
              path="/quiz"
              element={
                <Suspense fallback={<div className="p-4">Đang tải trang trắc nghiệm…</div>}>
                  <Quiz />
                </Suspense>
              }
            />
            {/* Bất kỳ /quiz/* lạ sẽ về /quiz */}
            <Route path="*" element={<Navigate to="/quiz" replace />} />
          </Routes>
        </main>
      ) : (
        // Nhánh còn lại dùng Auth + Header/Footer như cũ
        <AuthProvider>
          <Header />
          <Toaster position="top-center" toastOptions={{ duration: 1800 }} containerStyle={{ top: 70 }} />

          <main className="flex-1 overflow-auto pb-[calc(72px+env(safe-area-inset-bottom))]">
            <Routes>
              {/* Public */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/services/:id" element={<ServiceDetail />} />
              <Route path="/dang-tin" element={<PostForm />} />
              <Route path="/gioi-thieu" element={<GioiThieu />} />

              {/* Admin */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute rights={[]}>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={
                    <ProtectedRoute rights={rightsMap["/admin"]}>
                      <AdminHome />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="services"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/services"]}>
                      <AdminServices />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/users"]}>
                      <AdminUsers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="maintenance"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/maintenance"]}>
                      <AdminMaintenance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="maintenance/tools"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/maintenance"]}>
                      <AdminMaintenanceTools />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="posts"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/posts"]}>
                      <AdminPosts />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          <Footer />
        </AuthProvider>
      )}
    </div>
  );
}

export default function App() {
  return <AppShell />;
}
